import type { NavigateFunction } from "react-router-dom";
import AppRoutes from "@/constants/routes/AppRoutes";

let registeredNavigate: NavigateFunction | undefined;

/**
 * sessionStorage copy of the page the Auth0 callback should continue to. Written by every
 * handoff, read and cleared by the next sign-in attempt (`takePendingReturnTo`).
 */
export const AUTH_PENDING_RETURN_TO_KEY = "lara.auth.pendingReturnTo";

/**
 * The location to come back to after a re-login, read at the moment of failure.
 * Shared by every path that sends the user through sign-in so they can't drift —
 * a re-login that forgets this is what dropped users on the dashboard (LAR-361).
 */
export const currentReturnToPath = (): string =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

/** Called from AppRouter so Auth0's onRedirectCallback can use React Router navigation. */
export const setAuthRedirectNavigate = (
  navigate: NavigateFunction | undefined
): void => {
  registeredNavigate = navigate;
};

/**
 * Turn Auth0 `returnTo` (path or same-origin absolute URL) into a React Router location string.
 * Other origins are rejected to open redirects — fall back to dashboard.
 */
export function normalizeInternalNavigateTarget(to: string): string {
  const trimmed = to.trim();
  if (!trimmed) {
    const fallback = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    return fallback || "/";
  }

  try {
    const url = new URL(trimmed, window.location.origin);
    if (url.origin !== window.location.origin) {
      return AppRoutes.dashboard;
    }
    const path = `${url.pathname}${url.search}${url.hash}`;
    return path || "/";
  } catch {
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  }
}

/**
 * After Auth0 `handleRedirectCallback`, continue the SPA on `/auth` with `returnTo` in location state
 * so `SignInView` can decode the token, handle `first_login` / onboarding, then `finalizeLogin`.
 *
 * The page is stored as well, not only when no `navigate` is registered: the router applies this
 * navigation in a transition, and auth0-react clears `isLoading` right after this returns, so the
 * index route renders first on the callback URL and its stateless `<Navigate to="/auth">` replaces
 * this one, state and all (LAR-565).
 */
export function completeAuth0BrowserHandoff(
  appStateReturnTo: string | undefined,
  urlFallback: string
): void {
  const raw =
    appStateReturnTo && appStateReturnTo.trim() ? appStateReturnTo : urlFallback;
  const normalized = normalizeInternalNavigateTarget(raw);

  try {
    sessionStorage.setItem(AUTH_PENDING_RETURN_TO_KEY, normalized);
  } catch {
    /* private mode / quota */
  }

  if (registeredNavigate) {
    registeredNavigate(AppRoutes.auth, {
      replace: true,
      state: { returnTo: normalized },
    });
    return;
  }

  window.location.replace(`${window.location.origin}${AppRoutes.auth}`);
}

/**
 * The page the last Auth0 handoff stored, removed as it is read so it only ever steers the
 * sign-in attempt that follows that callback.
 */
export function takePendingReturnTo(): string | undefined {
  try {
    const pending = sessionStorage.getItem(AUTH_PENDING_RETURN_TO_KEY);
    sessionStorage.removeItem(AUTH_PENDING_RETURN_TO_KEY);
    return pending ?? undefined;
  } catch {
    return undefined; /* private mode */
  }
}
